<template>
  <div class="editor-wrapper">
    <editor v-model="content" :tinymce-script-src="tinymceScriptSrc" :init="mergedInit" />
  </div>
</template>

<script>
import Editor from '@tinymce/tinymce-vue'
import 'tinymce/skins/ui/oxide/skin.min.css'

export default {
  name: 'DtTinymce',
  components: {
    Editor // 这里注册名称为小写 editor
  },
  props: {
    // v-model 绑定值
    value: {
      type: String,
      default: ''
    },
    // 允许用户传入自定义 config 配置，合并到默认配置中
    config: {
      type: Object,
      default: () => ({})
    },
    // 是否内嵌模式
    inline: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      content: this.value,
      tinymceScriptSrc: '/tinymce/tinymce.min.js' // 指向你本地的 TinyMCE
    }
  },
  computed: {
    mergedInit() {
      // 默认配置，可根据需要调整
      const defaultInit = {
        menubar: false,
        // toolbar 中放置内置字号下拉 fontsizeselect 以及自定义按钮 customFontSize
        toolbar:
          'undo redo | bold italic underline strikethrough | \
                  styleselect | fontsizeselect | customFontSize | \
                  alignleft aligncenter alignright alignjustify | \
                  subscript superscript  hr  charmap | \
                  forecolor backcolor | removeformat | help',
        fontsize_formats: '8px 10px 12px 14px 16px 18px 20px 22px 24px 36px 48px', // 推荐保留一部分常用选项
        content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px; line-height:1.5; }'
      }

      // 如果不是内嵌模式，多一些配置
      if (!this.inline) {
        Object.assign(defaultInit, {
          height: 600,
          toolbar_mode: 'wrap',
          plugins: [
            'autoresize advlist anchor autolink autosave code codesample directionality emoticons fullscreen hr insertdatetime link lists media nonbreaking noneditable pagebreak paste preview print save searchreplace tabfocus template textpattern visualblocks visualchars'
          ],
          toolbar:
            'undo redo | styleselect | fontsizeselect | customFontSize | lineHeightMenu | bold italic underline strikethrough blockquote removeformat subscript superscript hr alignleft aligncenter alignright bullist numlist link preview  pagebreak insertdatetime emoticons forecolor backcolor fullscreen'
        })
      }

      const userInit = this.config || {}

      // 处理 setup: 先执行默认 setup，再执行用户提供的 setup
      const defaultSetup = (editor) => {
        // 自定义字号按钮: 仅允许正整数输入，自动添加 px
        editor.ui.registry.addButton('customFontSize', {
          text: '自定义字号',
          tooltip: '请输入正整数(单位 px)',
          onAction: () => {
            editor.windowManager.open({
              title: '设置自定义字号',
              body: {
                type: 'panel',
                items: [
                  {
                    type: 'input',
                    name: 'fontsize',
                    label: '字号（仅输入正整数，自动以 px 为单位）'
                  }
                ]
              },
              buttons: [
                { type: 'cancel', text: '取消' },
                { type: 'submit', text: '应用', primary: true }
              ],
              onSubmit(api) {
                const data = api.getData()
                const raw = data.fontsize && data.fontsize.trim()
                const intRegex = /^[1-9]\d*$/
                if (!raw || !intRegex.test(raw)) {
                  editor.windowManager.alert('请输入正整数，例如 12、16、24 等，不需要输入单位。')
                  return
                }
                const sizeValue = raw + 'px'
                const formatName = 'customfontsize_' + raw
                editor.formatter.register(formatName, {
                  inline: 'span',
                  styles: { 'font-size': sizeValue }
                })
                editor.formatter.apply(formatName)
                api.close()
              }
            })
          }
        })

        // 固定行高选项和自定义行高菜单按钮
        editor.ui.registry.addMenuButton('lineHeightMenu', {
          text: '行高',
          tooltip: '设置行间距',
          fetch: (callback) => {
            const items = []
            // 常见固定选项，可根据需求调整
            const fixedOptions = [
              { text: '1.0', value: '1' },
              { text: '1.25', value: '1.25' },
              { text: '1.5', value: '1.5' },
              { text: '2.0', value: '2' },
              { text: '2.5', value: '2.5' },
              { text: '3.0', value: '3.0' }
            ]
            fixedOptions.forEach((opt) => {
              items.push({
                type: 'menuitem',
                text: opt.text,
                onAction: () => {
                  const formatName = 'lineheight_' + opt.value.replace('.', '_')
                  editor.formatter.register(formatName, {
                    block: 'p',
                    styles: { 'line-height': opt.value }
                  })
                  editor.formatter.apply(formatName)
                }
              })
            })
            // 分隔符
            items.push({ type: 'separator' })
            // 自定义选项
            items.push({
              type: 'menuitem',
              text: '自定义行高',
              onAction: () => {
                editor.windowManager.open({
                  title: '设置自定义行间距',
                  body: {
                    type: 'panel',
                    items: [
                      {
                        type: 'input',
                        name: 'lineheight',
                        label: '行间距（输入数字，如 1.25、1.5、2）'
                      }
                    ]
                  },
                  buttons: [
                    { type: 'cancel', text: '取消' },
                    { type: 'submit', text: '应用', primary: true }
                  ],
                  onSubmit(api) {
                    const data = api.getData()
                    let raw = data.lineheight && data.lineheight.trim()
                    if (!raw || !/^\d+(\.\d+)*$/.test(raw)) {
                      editor.windowManager.alert('请输入有效行间距值，例如 1.25、1.5、2')
                      return
                    }
                    const key = raw.replace(/\./g, '_').replace(/[^\w]/g, '')
                    const formatName = 'customlineheight_' + key
                    editor.formatter.register(formatName, {
                      block: 'p',
                      styles: { 'line-height': raw }
                    })
                    editor.formatter.apply(formatName)
                    api.close()
                  }
                })
              }
            })
            callback(items)
          }
        })
      }

      // 合并 setup
      let mergedSetup
      if (typeof userInit.setup === 'function') {
        mergedSetup = (editor) => {
          defaultSetup(editor)
          userInit.setup(editor)
        }
      } else {
        mergedSetup = defaultSetup
      }

      // 合并默认 config 与用户 init，但将 setup 替换为 mergedSetup
      const merged = {
        ...defaultInit,
        ...userInit,
        inline: this.inline,
        setup: mergedSetup
      }
      return merged
    }
  },
  watch: {
    value: {
      handler(newVal) {
        if (newVal !== this.content) {
          this.content = newVal
        }
      },
      immediate: true
    },
    content(newVal) {
      if (newVal !== this.value) {
        // 向外触发 input 事件，以支持 v-model
        this.$emit('input', newVal)
        this.$emit('change', newVal)
      }
    }
  }
}
</script>

<style lang="scss" scoped></style>
